import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Check, Award, Star, Sparkles } from 'lucide-react';
import InteractiveLesson from './InteractiveLesson';
import { lesson1Data } from './interactiveLessonData';
import { onAuthChange } from './firebase/authService';
import { completeCourseLesson } from './firebase/progressService';
import * as analytics from './analytics';

const BibleOriginLanding = () => {
  const [showLesson, setShowLesson] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthChange((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Check if user already completed this lesson anonymously
  useEffect(() => {
    const anonymousProgress = localStorage.getItem('bible_origin_completed');
    if (anonymousProgress) {
      setLessonCompleted(true);
    }
  }, []);

  const handleStartLesson = () => {
    analytics.event({
      action: 'start_lesson',
      category: 'engagement',
      label: 'bible_origin_landing'
    });
    setShowLesson(true);
  };

  const handleLessonComplete = async (xpEarned) => {
    setLessonCompleted(true);
    
    // Track lesson completion
    analytics.event({
      action: 'complete_lesson',
      category: 'engagement',
      label: 'bible_origin_lesson_1',
      value: xpEarned
    });
    
    // If user is logged in, save progress to their account
    if (user) {
      try {
        await completeCourseLesson(user.uid, 'bible-history', 0, xpEarned);
      } catch (error) {
        console.error('Error saving progress:', error);
      }
      // If already logged in, exit lesson view
      setShowLesson(false);
    } else {
      // Save anonymous progress to localStorage
      localStorage.setItem('bible_origin_completed', JSON.stringify({
        completed: true,
        timestamp: new Date().toISOString(),
        xp: xpEarned
      }));
      
      // Immediately show signup prompt for anonymous users
      setShowLesson(false);
      setShowSignupPrompt(true);
    }
  };

  const handleSignUp = () => {
    // Track signup conversion
    analytics.event({
      action: 'click_signup',
      category: 'conversion',
      label: 'bible_origin_landing'
    });
    
    // Store intention to return to Bible History after signup
    localStorage.setItem('signup_redirect', 'bible-history');
    navigate('/auth?mode=signup&redirect=bible-history');
  };

  const handleViewAllLessons = () => {
    if (user) {
      navigate('/bible-history');
    } else {
      handleSignUp();
    }
  };

  const handleExitLesson = () => {
    setShowLesson(false);
  };

  // If user is viewing the lesson, show InteractiveLesson component
  if (showLesson) {
    return (
      <InteractiveLesson
        lessonData={lesson1Data}
        onComplete={handleLessonComplete}
        onExit={handleExitLesson}
      />
    );
  }

  // Signup prompt modal after lesson completion
  if (showSignupPrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8 md:p-12 text-center animate-fade-in">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-4">
              <Check className="w-10 h-10 text-white" />
            </div>
          </div>

          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Great Job! 🎉
          </h2>

          <p className="text-xl text-gray-700 mb-6">
            You've completed your first lesson!
          </p>

          <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-6 mb-8">
            <p className="text-lg font-semibold text-gray-800 mb-4">
              Ready to continue your journey?
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              <div className="flex items-start gap-3">
                <Book className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-gray-800">7 More Lessons</p>
                  <p className="text-sm text-gray-600">Complete the full journey</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Star className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-gray-800">Track Progress</p>
                  <p className="text-sm text-gray-600">Earn XP and rewards</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Award className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-gray-800">Smart Reviews</p>
                  <p className="text-sm text-gray-600">Never forget what you learn</p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleSignUp}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl text-lg font-bold hover:shadow-2xl transition-all duration-300 hover:scale-105 mb-4"
          >
            Create Free Account & Continue
          </button>

          <p className="text-sm text-gray-500">
            100% free • No credit card required • Takes 30 seconds
          </p>
        </div>
      </div>
    );
  }

  // Main landing page
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight mt-8">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Where Did the Bible
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Come From?
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
            Discover the fascinating 3,500-year journey from ancient campfire stories to the book in your hands today.
          </p>

          <button
            onClick={handleStartLesson}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl text-xl font-bold hover:shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-3"
          >
            <Book className="w-6 h-6" />
            Start Free Lesson
          </button>

          <p className="text-sm text-gray-500 mt-4">
            ⏱️ 6 minutes • No signup required
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4">
              <Book className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-800">Interactive Learning</h3>
            <p className="text-gray-600">
              Engaging lessons with quizzes, matching games, and visual timelines. Learn by doing, not just reading.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4">
              <Star className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-800">Track Progress</h3>
            <p className="text-gray-600">
              Earn XP, complete challenges, and see your knowledge grow. Smart review system ensures you remember what you learn.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
            <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-4">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-800">AI-Powered Learning</h3>
            <p className="text-gray-600">
              Curated lesson library plus custom AI-generated learning paths tailored to your interests and goals.
            </p>
          </div>
        </div>

        {/* What You'll Learn Section */}
        <div className="mt-20 bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            What You'll Learn
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "How oral traditions became written Scripture",
              "The materials and methods ancient scribes used",
              "Why we have different Bible translations",
              "How the books of the Bible were chosen",
              "The Dead Sea Scrolls discovery",
              "Early church councils and canon formation",
              "Manuscript preservation through history",
              "Modern Bible translation process"
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <p className="text-lg text-gray-700">{item}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <button
              onClick={handleStartLesson}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl text-xl font-bold hover:shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-3"
            >
              <Book className="w-6 h-6" />
              Begin Your Journey
            </button>
          </div>
        </div>

        {/* If lesson was completed anonymously, show completion badge */}
        {lessonCompleted && !user && (
          <div className="mt-12 bg-gradient-to-r from-green-100 to-green-200 rounded-2xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">
              You've Completed Lesson 1!
            </h3>
            <p className="text-gray-700 mb-6">
              Create a free account to unlock 7 more lessons and track your progress.
            </p>
            <button
              onClick={handleSignUp}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl text-lg font-bold hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Create Free Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BibleOriginLanding;
